import React from "react";
import { Helmet } from "react-helmet-async";
import {NavBar} from "@/components/navbar";
import {Footer} from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const AIFMDLanding23 = () => {
  return (
    <div className="relative min-h-screen overflow-hidden">
      <Helmet>
        <title>Strategic AIFMD Compliance Solutions | Verdea AI</title>
        <meta 
          name="description" 
          content="Transform your AIFMD compliance processes with our AI-powered solutions. Reduce complexity, save time, and improve accuracy." 
        />
      </Helmet>
      
      {/* Background Elements */}
      <div className="fixed inset-0 z-0 pointer-events-none bg-gradient-to-b from-slate-50 to-white">
        <div
          className="absolute top-0 right-0 w-[600px] h-[600px] blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.4) 0%, rgba(16, 185, 129, 0) 70%)',
            transform: 'translate(30%, -30%)'
          }}
        ></div>
        <div
          className="absolute bottom-0 left-0 w-[600px] h-[600px] blur-3xl opacity-20"
          style={{
            background: 'radial-gradient(circle at center, rgba(59, 130, 246, 0.3) 0%, rgba(59, 130, 246, 0) 70%)',
            transform: 'translate(-30%, 30%)'
          }}
        ></div>
      </div>
      
      {/* Navbar - landing mode hides platform, tools and login buttons */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200/80">
        <NavBar landingMode={true} />
      </header>
      
      <main className="relative z-10">
        {/* Hero section */}
        <section className="py-20 md:py-32">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-12 items-center">
                <div className="lg:w-1/2 space-y-8">
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900">
                      Simplify <span className="text-emerald-600">AIFMD</span> compliance with intelligent automation
                    </h1>
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    <p className="text-xl text-gray-600 max-w-2xl">
                      Our AI-powered platform helps financial institutions reduce compliance costs, 
                      minimize risks, and accelerate time-to-market for AIFMD-regulated products.
                    </p>
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="flex flex-col sm:flex-row gap-4"
                  >
                    <Button 
                      size="lg" 
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-8 py-6"
                      asChild
                    >
                      <Link to="/contact">Contact Our Experts</Link>
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 rounded-md px-8 py-6"
                    >
                      Watch Demo
                    </Button>
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="pt-4"
                  >
                    <p className="text-sm text-gray-500 mb-4">Trusted by leading financial institutions</p>
                    <div className="flex flex-wrap gap-8 items-center">
                      <img src="/logos/ant.png" alt="Ant Financial" className="h-8" />
                      <img src="/logos/ejaso.png" alt="EJASO ETL Global" className="h-8" />
                      <img src="/logos/vba.png" alt="VBA" className="h-8" />
                      <img src="/logos/greentech.jpg" alt="GreenTech" className="h-8" />
                    </div>
                  </motion.div>
                </div>
                
                <div className="lg:w-1/2">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6 }}
                    className="relative"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-2xl blur-[2px] opacity-20 transform rotate-3"></div>
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200/80 relative">
                      <img 
                        src="/screens/aifmd/aifguard_1.png"
                        alt="Verdea AI Dashboard" 
                        className="w-full h-auto object-cover"
                      />
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Stats section */}
        <section className="py-16 bg-gray-50 border-y border-gray-200/60">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {[
                  {
                    value: "85%",
                    label: "Reduction in manual review time"
                  },
                  {
                    value: "99.2%",
                    label: "Compliance accuracy rate"
                  },
                  {
                    value: "60%",
                    label: "Lower compliance costs"
                  },
                  {
                    value: "3x",
                    label: "Faster time-to-market"
                  }
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white p-8 rounded-xl shadow-md border border-gray-200/50 text-center"
                  >
                    <div className="text-4xl font-bold mb-2 text-emerald-600">{stat.value}</div>
                    <p className="text-gray-600">{stat.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        {/* Features section */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">Comprehensive AIFMD Compliance Solutions</h2>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                    Our platform combines AI technology with regulatory expertise to deliver end-to-end compliance solutions.
                  </p>
                </motion.div>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
                {[
                  {
                    title: "Intelligent Document Analysis",
                    description: "Our AI analyzes all your marketing materials and financial documents for AIFMD compliance, flagging issues before they become problems.",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-emerald-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    )
                  },
                  {
                    title: "Cross-Border Compliance",
                    description: "Navigate the complexities of marketing across EU jurisdictions with our platform's country-specific AIFMD compliance checks.",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-emerald-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )
                  },
                  {
                    title: "Regulatory Reporting",
                    description: "Generate comprehensive AIFMD reports with one click, saving days of manual work and ensuring all regulatory requirements are met.",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-emerald-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    )
                  },
                  {
                    title: "Risk Monitoring Dashboard",
                    description: "Real-time compliance monitoring identifies emerging risks before they become regulatory issues, with customizable alerts and reports.",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-emerald-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    )
                  },
                  {
                    title: "Marketing Workflow Integration",
                    description: "Seamlessly integrate compliance checks into your existing marketing workflows, eliminating bottlenecks without sacrificing compliance.",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-emerald-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                      </svg>
                    )
                  },
                  {
                    title: "Audit-Ready Documentation",
                    description: "Maintain a complete audit trail of all compliance activities, with timestamped records and version history for regulatory inspections.",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-emerald-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )
                  }
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-gray-50 p-8 rounded-xl border border-gray-200/60"
                  >
                    {feature.icon}
                    <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        {/* How it works section */}
        <section className="py-24 bg-gray-50 border-y border-gray-200/60">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">How Our AIFMD Compliance Platform Works</h2>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                    A streamlined approach to managing complex regulatory requirements
                  </p>
                </motion.div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-16 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <img 
                    src="/screens/aifmd/aifguard_2.png"
                    alt="Verdea AI Platform" 
                    className="rounded-2xl shadow-lg w-full h-auto border border-gray-200/80"
                  />
                </motion.div>
                
                <div>
                  <ol className="space-y-8">
                    {[
                      {
                        title: "Intelligent Data Processing",
                        description: "Our platform ingests all your marketing materials, financial documents, and regulatory filings for comprehensive analysis."
                      },
                      {
                        title: "AI-Powered Analysis",
                        description: "Advanced machine learning models trained on AIFMD regulations identify compliance issues with 99.2% accuracy."
                      },
                      {
                        title: "Risk Identification",
                        description: "Potential compliance issues are identified and prioritized based on regulatory impact and urgency."
                      },
                      {
                        title: "Actionable Recommendations",
                        description: "Clear guidance on how to address each compliance issue, with specific references to relevant regulations."
                      },
                      {
                        title: "Compliance Documentation",
                        description: "Automated generation of all required regulatory documentation, ready for submission to authorities."
                      }
                    ].map((step, index) => (
                      <motion.li 
                        key={index} 
                        className="flex gap-4"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <h3 className="text-xl font-bold mb-1">{step.title}</h3>
                          <p className="text-gray-600">{step.description}</p>
                        </div>
                      </motion.li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Testimonials section */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">What Our Clients Say</h2>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                    Hear from financial institutions that have transformed their AIFMD compliance processes
                  </p>
                </motion.div>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  {
                    quote: "Verdea AI's platform has reduced our AIFMD compliance review times by 85% while improving accuracy. It's been a game-changer for our marketing team.",
                    author: "Sarah Johnson",
                    role: "Chief Compliance Officer",
                    company: "European Asset Management"
                  },
                  {
                    quote: "The cross-border compliance features have allowed us to expand into new EU markets with confidence, knowing our marketing materials are fully compliant.",
                    author: "Michael Chen",
                    role: "Head of Marketing",
                    company: "Global Alternatives Fund"
                  },
                  {
                    quote: "The automated reporting alone has saved us hundreds of hours per quarter. The ROI on this platform was evident within the first month.",
                    author: "Thomas Weber",
                    role: "Managing Director",
                    company: "Alpine Capital Partners"
                  }
                ].map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-gray-50 p-8 rounded-xl border border-gray-200/60"
                  >
                    <div className="text-emerald-500 mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                      </svg>
                    </div>
                    <p className="text-gray-700 mb-6 italic">"{testimonial.quote}"</p>
                    <div>
                      <p className="font-bold">{testimonial.author}</p>
                      <p className="text-gray-500 text-sm">{testimonial.role}, {testimonial.company}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        {/* CTA section */}
        <section className="py-20 bg-gradient-to-r from-emerald-600 to-emerald-800 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to transform your AIFMD compliance process?</h2>
                <p className="text-xl opacity-90 mb-10 max-w-3xl mx-auto">
                  Our experts are ready to show you how our AI-powered platform can reduce costs, minimize risks, and accelerate your compliance workflows.
                </p>
                <div className="flex flex-col sm:flex-row gap-6 justify-center">
                  <Button 
                    size="lg" 
                    className="bg-white text-emerald-700 hover:bg-gray-100 rounded-md px-8 py-6"
                    asChild
                  >
                    <Link to="/contact">Contact Our Experts</Link>
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-white text-white hover:bg-emerald-700 rounded-md px-8 py-6"
                  >
                    Schedule a Demo
                  </Button>
                </div>
                <p className="mt-8 text-sm opacity-80">
                  Join hundreds of financial institutions already using our platform to streamline AIFMD compliance.
                </p>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      
      <footer>
        <Footer />
      </footer>
    </div>
  );
};

export default AIFMDLanding23;